import { Box, Button, CircularProgress, Dialog, Typography, useTheme, Chip, Divider } from '@mui/material'
import Slide from '@mui/material/Slide'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { makeGetRequest, makePostRequest } from 'utils/api'
import logo from '../images/logo.png'
import { Radar } from 'react-chartjs-2'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import RefreshIcon from '@mui/icons-material/Refresh'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

import {
  Chart as ChartJS,
  RadialLinearScale,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'

ChartJS.register(
  RadialLinearScale,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Title,
  Tooltip,
  Legend,
)

// Human-readable French labels for each domain key
const DOMAIN_LABELS = {
  Finance: 'Finance',
  Temps: 'Temps',
  Earth: 'Planète',
  Social: 'Social',
  Professionnel: 'Professionnel',
  Personnel: 'Personnel',
  Famille: 'Foyer',
  Administratif: 'Administratif',
}

// Snapshot source type labels
const SNAPSHOT_SOURCE_LABELS = {
  AUTO: 'Automatique',
  MANUAL: 'Manuel',
  ONBOARDING: 'Onboarding',
}

// Snapshot source type colors
const SNAPSHOT_SOURCE_COLORS = {
  AUTO: '#0089ff',
  MANUAL: '#fbca6d',
  ONBOARDING: '#4caf50',
}

// Color palette for multiple series
const SERIES_COLORS = [
  { bg: 'rgba(0, 137, 255, 0.20)', border: '#0089ff', point: '#0089ff' },
  { bg: 'rgba(251, 202, 109, 0.20)', border: '#fbca6d', point: '#fbca6d' },
  { bg: 'rgba(76, 175, 80, 0.20)', border: '#4caf50', point: '#4caf50' },
  { bg: 'rgba(156, 39, 176, 0.20)', border: '#9c27b0', point: '#9c27b0' },
]

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />
})

export default function GeneralReport() {
  const siteUser = useSelector((store) => store.user)
  const theme = useTheme()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [radarData, setRadarData] = useState(null)
  const [latestSnapshot, setLatestSnapshot] = useState(null)
  const [error, setError] = useState(null)
  const [regenerating, setRegenerating] = useState(false)

  useEffect(() => {
    fetchRadarData()
  }, [])

  const fetchRadarData = async () => {
    setLoading(true)
    setError(null)
    try {
      // Fetch radar data and latest snapshot in parallel
      const [radarRes, latestRes] = await Promise.all([
        makeGetRequest('/api/general/life-resume/radar/', {}, true),
        makeGetRequest('/api/general/life-resume/latest/', {}, true),
      ])

      if (radarRes?.status === 200 && radarRes.data) {
        setRadarData(radarRes.data)
      }
      if (latestRes?.status === 200 && latestRes.data) {
        setLatestSnapshot(latestRes.data)
      }

      // If no data at all, show empty state
      if (
        (!radarRes?.data?.series || radarRes.data.series.length === 0) &&
        latestRes?.status === 404
      ) {
        setError('no_data')
      }
    } catch (err) {
      console.error('Failed to fetch life resume data:', err)
      setError('fetch_error')
    } finally {
      setLoading(false)
    }
  }

  const handleRegenerate = async () => {
    setRegenerating(true)
    try {
      await makePostRequest('/api/general/life-resume/generate/', { force: true }, {}, true)
      await fetchRadarData()
    } catch (err) {
      console.error('Failed to regenerate snapshot:', err)
    } finally {
      setRegenerating(false)
    }
  }

  // Build chart.js data from API radar response
  const buildChartData = () => {
    if (!radarData?.domains || !radarData?.series?.length) return null

    const labels = radarData.domains.map((d) => DOMAIN_LABELS[d] || d)

    const datasets = radarData.series.map((s, i) => {
      const colors = SERIES_COLORS[i % SERIES_COLORS.length]
      return {
        label: s.label || s.snapshot_type,
        data: s.scores.map((v) => (v !== null ? v : 0)),
        fill: true,
        backgroundColor: colors.bg,
        borderColor: colors.border,
        pointBackgroundColor: colors.point,
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: colors.border,
        borderWidth: 2,
      }
    })

    return { labels, datasets }
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    scales: {
      r: {
        min: 0,
        max: 10,
        ticks: {
          stepSize: 2,
          display: true,
          color: 'rgba(255, 255, 255, 0.5)',
          backdropColor: 'transparent',
          font: { size: 11 },
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.15)',
          lineWidth: 1,
        },
        angleLines: {
          color: 'rgba(255, 255, 255, 0.15)',
        },
        pointLabels: {
          color: '#fbca6d',
          font: { size: 14, weight: '600' },
        },
      },
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          color: 'rgba(255, 255, 255, 0.85)',
          padding: 20,
          font: { size: 13 },
        },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.r}/10`,
        },
      },
      datalabels: {
        display: false,
      },
    },
  }

  const prenom = siteUser.user?.first_name || siteUser.user?.prenom || ''

  const chartData = buildChartData()

  // Format period display
  const formatPeriod = (snapshot) => {
    if (!snapshot) return ''
    const { period_type, period_year, period_month } = snapshot
    if (period_type === 'YEAR') {
      return `Année ${period_year}`
    }
    if (period_type === 'MONTH' && period_month) {
      const monthNames = [
        'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
        'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
      ]
      return `${monthNames[period_month - 1]} ${period_year}`
    }
    return `${period_year}`
  }

  // Format date display
  const formatDate = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString('fr-FR', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    })
  }

  return (
    <Dialog
      fullScreen
      open={true}
      TransitionComponent={Transition}
      sx={{
        '.MuiDialog-paper': {
          backgroundColor: theme.palette.appBar?.main || '#0a1929',
          color: 'white',
        },
      }}
    >
      <Box sx={{ minHeight: '100vh', pb: 6 }}>
        {/* Header */}
        <Box display="flex" alignItems="center" justifyContent="space-between" px={3} pt={2}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ color: 'white', textTransform: 'none' }}
          >
            Retour
          </Button>
          <img
            style={{ height: '50px', cursor: 'pointer' }}
            onClick={() => navigate('/')}
            src={logo}
            alt="LIFE"
          />
          <Box width={80} /> {/* spacer */}
        </Box>

        {/* Title */}
        <Box textAlign="center" mt={3} px={3}>
          <Typography variant="h5" fontWeight={700}>
            Bonjour <span style={{ color: '#fbca6d' }}>{prenom}</span>, bienvenue dans Life
            Analytics
          </Typography>
          <Typography
            variant="body1"
            mt={1}
            sx={{ color: 'rgba(255, 255, 255, 0.75)', maxWidth: 600, mx: 'auto' }}
          >
            La roue de la vie Life vous offre une vue d'ensemble de votre équilibre personnel,
            intégrant les aspects essentiels de votre existence.
          </Typography>
        </Box>

        {/* Content */}
        <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4, px: { xs: 2, md: 4 } }}>
          <Box
            sx={{
              bgcolor: 'rgba(40, 56, 77, 0.6)',
              borderRadius: 4,
              p: { xs: 3, md: 5 },
              backdropFilter: 'blur(10px)',
            }}
          >
            {/* Section header */}
            <Typography variant="h6" fontWeight={700} mb={1}>
              ÉQUILIBRE GLOBAL
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: '#fbca6d', fontStyle: 'italic', mb: 4 }}
            >
              &ldquo;L'équilibre, c'est de ne laisser tomber aucune balle dans le grand jeu de
              la vie.&rdquo; — Brian Dyson
            </Typography>

            {/* Snapshot metadata header */}
            {latestSnapshot && !loading && !error && (
              <Box 
                sx={{ 
                  mb: 4, 
                  p: 2.5, 
                  bgcolor: 'rgba(255,255,255,0.03)',
                  borderRadius: 2,
                  border: '1px solid rgba(255,255,255,0.08)'
                }}
              >
                <Box display="flex" flexWrap="wrap" gap={2} alignItems="center" justifyContent="space-between">
                  <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
                    {/* Period */}
                    <Box display="flex" alignItems="center" gap={1}>
                      <CalendarTodayIcon sx={{ fontSize: 18, color: '#fbca6d' }} />
                      <Typography variant="body2" fontWeight={600}>
                        {formatPeriod(latestSnapshot)}
                      </Typography>
                    </Box>

                    {/* Source type */}
                    <Chip
                      label={SNAPSHOT_SOURCE_LABELS[latestSnapshot.snapshot_type] || latestSnapshot.snapshot_type}
                      size="small"
                      sx={{
                        bgcolor: SNAPSHOT_SOURCE_COLORS[latestSnapshot.snapshot_type] || '#666',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: 11,
                      }}
                    />

                    {/* Created date */}
                    {latestSnapshot.created_at && (
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                        Créé le {formatDate(latestSnapshot.created_at)}
                      </Typography>
                    )}
                  </Box>

                  {/* Global score */}
                  {latestSnapshot.global_score !== null && latestSnapshot.global_score !== undefined && (
                    <Box textAlign="right">
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', display: 'block' }}>
                        Score global
                      </Typography>
                      <Typography variant="h4" fontWeight={700} sx={{ color: '#fbca6d' }}>
                        {latestSnapshot.global_score}
                        <Typography component="span" variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                          /10
                        </Typography>
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Frozen text if available */}
                {latestSnapshot.frozen_text && (
                  <>
                    <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.08)' }} />
                    <Box display="flex" gap={1} alignItems="flex-start">
                      <InfoOutlinedIcon sx={{ fontSize: 18, color: '#0089ff', mt: 0.3 }} />
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', lineHeight: 1.6 }}>
                        {latestSnapshot.frozen_text}
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>
            )}

            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
                <CircularProgress sx={{ color: '#0089ff' }} />
              </Box>
            ) : error === 'no_data' ? (
              /* Empty state */
              <Box textAlign="center" py={8}>
                <Typography variant="h6" mb={2}>
                  Aucune donnée disponible
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: 'rgba(255,255,255,0.6)', mb: 3, maxWidth: 420, mx: 'auto' }}
                >
                  Complétez l'onboarding pour générer votre premier bilan Life Resume, ou
                  générez-le manuellement.
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleRegenerate}
                  disabled={regenerating}
                  startIcon={regenerating ? <CircularProgress size={18} /> : <RefreshIcon />}
                  sx={{
                    bgcolor: '#0089ff',
                    textTransform: 'none',
                    '&:hover': { bgcolor: '#0070cc' },
                  }}
                >
                  {regenerating ? 'Génération…' : 'Générer mon bilan'}
                </Button>
              </Box>
            ) : error === 'fetch_error' ? (
              <Box textAlign="center" py={8}>
                <Typography variant="body1" color="error">
                  Erreur lors du chargement. Veuillez réessayer.
                </Typography>
                <Button onClick={fetchRadarData} sx={{ color: '#0089ff', mt: 2 }}>
                  Réessayer
                </Button>
              </Box>
            ) : chartData ? (
              <>
                {/* Radar chart */}
                <Box
                  display="flex"
                  justifyContent="center"
                  sx={{
                    maxWidth: { xs: 350, sm: 450, md: 520 },
                    mx: 'auto',
                    my: 3,
                  }}
                >
                  <Radar data={chartData} options={chartOptions} />
                </Box>

                {/* Domain score cards */}
                {latestSnapshot?.domain_scores && (
                  <Box mt={5}>
                    <Typography variant="subtitle1" fontWeight={600} mb={2}>
                      Détail par domaine
                    </Typography>
                    <Box
                      display="grid"
                      gridTemplateColumns={{
                        xs: '1fr',
                        sm: '1fr 1fr',
                        md: 'repeat(4, 1fr)',
                      }}
                      gap={2}
                    >
                      {latestSnapshot.domain_scores.map((ds) => (
                        <Box
                          key={ds.domain}
                          sx={{
                            bgcolor: 'rgba(255,255,255,0.05)',
                            borderRadius: 2,
                            p: 2,
                            textAlign: 'center',
                            border: '1px solid rgba(255,255,255,0.08)',
                            transition: 'all 0.2s',
                            '&:hover': {
                              bgcolor: 'rgba(255,255,255,0.08)',
                              transform: 'translateY(-2px)',
                            }
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: '#fbca6d', fontWeight: 600, textTransform: 'uppercase' }}
                          >
                            {DOMAIN_LABELS[ds.domain] || ds.domain}
                          </Typography>
                          <Typography variant="h4" fontWeight={700} mt={0.5}>
                            {ds.final_score !== null ? ds.final_score : '—'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                            {ds.has_evaluation ? '/10' : 'Pas de données'}
                          </Typography>
                          
                          {/* Frozen domain text if available */}
                          {ds.frozen_text && (
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                display: 'block',
                                mt: 1.5,
                                pt: 1.5,
                                borderTop: '1px solid rgba(255,255,255,0.08)',
                                color: 'rgba(255,255,255,0.65)',
                                fontStyle: 'italic',
                                lineHeight: 1.4
                              }}
                            >
                              {ds.frozen_text}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Regenerate button */}
                <Box textAlign="center" mt={4}>
                  <Button
                    size="small"
                    onClick={handleRegenerate}
                    disabled={regenerating}
                    startIcon={regenerating ? <CircularProgress size={14} /> : <RefreshIcon />}
                    sx={{ color: 'rgba(255,255,255,0.6)', textTransform: 'none', fontSize: 13 }}
                  >
                    {regenerating ? 'Régénération…' : 'Régénérer le bilan'}
                  </Button>
                </Box>
              </>
            ) : null}
          </Box>
        </Box>
      </Box>
    </Dialog>
  )
}
